/* istanbul ignore file */
import React, { useState, useMemo } from 'react'
import instrument from '@adc/instrument'
import * as blueprint from '@adc/blueprint'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'
import { submit } from 'redux-form'
import _ from 'lodash'
import moment from 'moment'
import {
  Button,
  Icon,
  Popup,
  Modal,
  TutorialTooltip,
  Grid,
  useApplicationModals,
  constants,
} from '@adc/ui-adc'
import classnames from 'classnames/bind'
import { getUpdatedSortObject } from '../../util/sorting'
import { authTypes } from '../../constants/statics'
import {
  PROPERTY_ELM_ID,
  ASSET_TYPE_ELM_ID,
  AUCTION_TYPE_ELM_ID,
  CONDITION_ELM_ID,
  FEATURED_ELM_ID,
  MORE_ELM_ID,
  ALL_ELM_ID,
  PROPERTY,
  ASSET_TYPE,
  AUCTION_TYPE,
  CONDITION,
  FEATURED,
  MORE,
  ALL,
} from '../../constants/filters'
import { ConditionFiltersForm } from '../filtersHeader/conditionFilters'
import { MoreFiltersForm } from '../filtersHeader/moreFilters'
import { FeaturedFiltersForm } from '../filtersHeader/featuredFilters'
import { PropertyFiltersForm } from '../filtersHeader/propertyFilters'
import { AuctionTypeFiltersForm } from '../filtersHeader/auctionTypeFilters'
import { AssetTypeFiltersForm } from '../filtersHeader/assetTypeFilters'
import AllFiltersForm, {
  BASE_ELM_ID as allFiltersBaseElmId,
} from '../filtersHeader/allFilters'
import styles from './styles.scss'
const cx = classnames.bind(styles)

export const FILTER_DEBOUNCE_MS = 600

const { Row, Column } = Grid
const BASE_ELM_ID = 'filters_header'
const SRP_VIEW_ACTION_NAME = 'srp_view_action'
const { ColorNames } = constants

const Filters = ({
  uiFilterGroups,
  defaultUiFilterGroups,
  isSearchSaved,
  onFilterChange,
  featureFlags,
  updateSorting: updateSortingOrig,
  currentTutorial,
  marketingTagsConfig,
  onSaveSearch,
  reduxFormSubmit,
}) => {
  const submitFilterFormDebounced = useMemo(
    () =>
      _.debounce(formName => {
        reduxFormSubmit(formName)
      }, FILTER_DEBOUNCE_MS),
    [reduxFormSubmit]
  )
  const submitFilterForm = (...args) => {
    setLastSubmit(moment())
    submitFilterFormDebounced(...args)
  }
  const [openFilter, setOpenFilter] = useState(null)
  const [lastSubmit, setLastSubmit] = useState(null)

  const modals = useApplicationModals()

  const signup = () =>
    modals.open('authentication', { authInitialActive: authTypes.SIGNUP })

  const toggleView = e => {
    const { mapView: isMapView } = uiFilterGroups.mapFilterGroup
    onFilterChange({ mapView: !isMapView })
    instrument.increment(SRP_VIEW_ACTION_NAME, 1, [
      `name:toggle_map_view`,
      `map_view:${isMapView}`,
      `view_category:mobile`,
    ])
  }

  const toggleMap = _ => {
    const { isMapShown } = uiFilterGroups.mapFilterGroup
    onFilterChange({ isMapShown: !isMapShown })
    instrument.increment(SRP_VIEW_ACTION_NAME, 1, [
      `name:toggle_map_view`,
      `map_view:${isMapShown}`,
      `view_category:desktop`,
    ])
  }

  const clearFiltersAction = showClearFilters => () => {
    if (showClearFilters) {
      onFilterChange()
    }
  }

  const handleOpen = (e, data) => {
    if (openFilter) {
      debounceUpdate(() => {
        setOpenFilter(data.filtername)
      })
    } else {
      setOpenFilter(data.filtername)
    }
  }

  const debounceUpdate = update => {
    const debounceWait = lastSubmit
      ? Math.max(0, FILTER_DEBOUNCE_MS - moment().subtract(lastSubmit))
      : 0

    // stall the close just a bit to ensure debounce is done
    setTimeout(update, debounceWait + 1)
  }

  const handleClose = (e, data) => {
    if (data.filtername === openFilter) {
      debounceUpdate(() => {
        // We check the currently open filter reamins the same to avoid accidentally closing a newly open filter
        if (data.filtername === openFilter) {
          setOpenFilter(null)
        }
      })
    }
  }

  const onModalClose = () => handleClose(null, { filtername: ALL })

  const renderFilterDefaultProps = () => {
    return {
      position: 'bottom center',
      flowing: true,
      on: 'click',
      positionFixed: true,
      /**
       * propertyFiltersInitialValues
       * The following line looks cryptic, but basically this turns off the use of transform: translate()
       * by the popper, and instead it falls back on normal left/top approach which is what we want as we
       * are using our own fixed positioning approach.
       *
       * Alternatively we could leave this out, leave out the position:fixed styling and just use the
       * pinned: true - feature, which works but is a little janky.  Turns out our approach is a better UX.
       */
      popperModifiers: { computeStyle: { gpuAcceleration: false } },
    }
  }

  const renderTrigger = (title, filterSetName, elmId) => {
    let defaultUiFilters = defaultUiFilterGroups[filterSetName]
    let uiFilters = uiFilterGroups[filterSetName]

    // TODO: this is weird because I split property filters into 2 groups
    // to make it easier for mobile since they have those beds, bath, sqft filters
    // in a separate section. We'll refactor this later when web and mobile migrate to
    // blueprint
    if (filterSetName === FILTER_GROUP_KEYS.propertyTypeFilterGroup) {
      defaultUiFilters = {
        ...defaultUiFilterGroups[FILTER_GROUP_KEYS.propertyTypeFilterGroup],
        ...defaultUiFilterGroups[FILTER_GROUP_KEYS.propertyInfoFilterGroup],
      }

      uiFilters = {
        ...uiFilterGroups[FILTER_GROUP_KEYS.propertyTypeFilterGroup],
        ...uiFilterGroups[FILTER_GROUP_KEYS.propertyInfoFilterGroup],
      }
    }

    const isChanged = filterSetName
      ? blueprint.search.utils.getIsFilterSetChanged(
          defaultUiFilters,
          uiFilters
        )
      : showClearFilters

    const isFocused = openFilter === title
    return (
      <Button
        className={cx('popup-button', {
          'is-changed': isChanged,
          'is-focused': isFocused,
        })}
        role={isChanged ? 'primary' : 'secondary'}
        color={isChanged ? ColorNames.Action : ColorNames.Gray}
        elmId={`${BASE_ELM_ID}_${_.snakeCase(elmId)}`}
      >
        {title} <Icon name='chevron down' />
      </Button>
    )
  }

  const updateSorting = value => {
    const updatedSortValue = getUpdatedSortObject(
      value,
      uiFilterGroups.nearbySearchFilterGroup
    )
    updateSortingOrig(updatedSortValue)
  }

  const { mapView: isMapView, isMapShown } = uiFilterGroups.mapFilterGroup

  const propertyTypeFilterGroup = {
    ...uiFilterGroups.propertyTypeFilterGroup,
    ...uiFilterGroups.propertyInfoFilterGroup,
  }

  const {
    conditionFilterGroup,
    assetTypeFilterGroup,
    auctionTypeFilterGroup,
    featuredFilterGroup,
    moreFilterGroup,
  } = uiFilterGroups

  const allFiltersInitialValues = {
    ...propertyTypeFilterGroup,
    ...conditionFilterGroup,
    ...assetTypeFilterGroup,
    ...auctionTypeFilterGroup,
    ...featuredFilterGroup,
    ...moreFilterGroup,
  }

  const shortSalesFilterEnabled =
    featureFlags['fe.resi.search.shortsales-enabled']

  const privateSellerFilterEnabled =
    featureFlags['fe.resi.search.private-seller-enabled']

  const inspectionAllowedFilterEnabled =
    featureFlags['fe.resi.search.enable-inspection-allowed']

  const leasebackRequiredFilterEnabled =
    featureFlags['fe.resi.search.enable-leaseback-required']

  const privateSellerEnabled = featureFlags['fe.resi.enable-private-seller']

  const { FILTER_GROUP_KEYS } = blueprint.search.constants

  const filterSetChangedMapping = useMemo(() => {
    const mapping = {}

    ;[
      FILTER_GROUP_KEYS.propertyTypeFilterGroup,
      FILTER_GROUP_KEYS.propertyInfoFilterGroup,
      FILTER_GROUP_KEYS.assetTypeFilterGroup,
      FILTER_GROUP_KEYS.auctionTypeFilterGroup,
      FILTER_GROUP_KEYS.conditionFilterGroup,
      FILTER_GROUP_KEYS.featuredFilterGroup,
      FILTER_GROUP_KEYS.moreFilterGroup,
    ].forEach(key => {
      const isChanged = blueprint.search.utils.getIsFilterSetChanged(
        defaultUiFilterGroups[key],
        uiFilterGroups[key]
      )

      mapping[key] = isChanged
    })

    return mapping
  }, [
    FILTER_GROUP_KEYS.propertyTypeFilterGroup,
    FILTER_GROUP_KEYS.propertyInfoFilterGroup,
    FILTER_GROUP_KEYS.assetTypeFilterGroup,
    FILTER_GROUP_KEYS.auctionTypeFilterGroup,
    FILTER_GROUP_KEYS.conditionFilterGroup,
    FILTER_GROUP_KEYS.featuredFilterGroup,
    FILTER_GROUP_KEYS.moreFilterGroup,
    defaultUiFilterGroups,
    uiFilterGroups,
  ])

  const showClearFilters = Object.values(filterSetChangedMapping).includes(true)

  const tooltipSelected = currentTutorial === 2

  const saveSearchButton = (
    <Button
      role='primary'
      disabled={isSearchSaved}
      elmId={`${BASE_ELM_ID}_save_search`}
      className={cx('save-search-button')}
      onClick={onSaveSearch}
    >
      {isSearchSaved ? 'Saved Search' : 'Save Search'}
    </Button>
  )

  return (
    <div
      data-elm-id={BASE_ELM_ID}
      className={cx('root', tooltipSelected && 'tooltip-selected')}
    >
      <div className={cx('filters-header')}>
        <TutorialTooltip
          tutorial={2}
          position='right center'
          mobilePosition='bottom center'
          elmId='filter_tutorial'
          header='Narrow Your Search'
          content='Use filters to improve your search results.'
        >
          <>
            <div className={cx('filters-options')}>
              <div>
                <Popup
                  {...renderFilterDefaultProps()}
                  trigger={renderTrigger(
                    PROPERTY,
                    FILTER_GROUP_KEYS.propertyTypeFilterGroup,
                    PROPERTY_ELM_ID
                  )}
                  open={openFilter === PROPERTY}
                  onClose={handleClose}
                  onOpen={handleOpen}
                  filtername={PROPERTY}
                  position='bottom left'
                >
                  <PropertyFiltersForm
                    initialValues={propertyTypeFilterGroup}
                    updateFilters={onFilterChange}
                    submitForms={submitFilterForm}
                    onClose={handleClose}
                  />
                </Popup>
              </div>
              <div>
                <Popup
                  {...renderFilterDefaultProps()}
                  trigger={renderTrigger(
                    ASSET_TYPE,
                    FILTER_GROUP_KEYS.assetTypeFilterGroup,
                    ASSET_TYPE_ELM_ID
                  )}
                  open={openFilter === ASSET_TYPE}
                  onClose={handleClose}
                  onOpen={handleOpen}
                  filtername={ASSET_TYPE}
                >
                  <AssetTypeFiltersForm
                    privateSellerEnabled={privateSellerEnabled}
                    shortSalesFilterEnabled={shortSalesFilterEnabled}
                    privateSellerFilterEnabled={privateSellerFilterEnabled}
                    initialValues={assetTypeFilterGroup}
                    updateFilters={onFilterChange}
                    submitForms={submitFilterForm}
                    onClose={handleClose}
                  />
                </Popup>
              </div>
              <div>
                <Popup
                  {...renderFilterDefaultProps()}
                  trigger={renderTrigger(
                    AUCTION_TYPE,
                    FILTER_GROUP_KEYS.auctionTypeFilterGroup,
                    AUCTION_TYPE_ELM_ID
                  )}
                  open={openFilter === AUCTION_TYPE}
                  onClose={handleClose}
                  onOpen={handleOpen}
                  filtername={AUCTION_TYPE}
                >
                  <AuctionTypeFiltersForm
                    initialValues={auctionTypeFilterGroup}
                    updateFilters={onFilterChange}
                    submitForms={submitFilterForm}
                    onClose={handleClose}
                  />
                </Popup>
              </div>
              <div>
                <Popup
                  {...renderFilterDefaultProps()}
                  trigger={renderTrigger(
                    CONDITION,
                    FILTER_GROUP_KEYS.conditionFilterGroup,
                    CONDITION_ELM_ID
                  )}
                  open={openFilter === CONDITION}
                  onClose={handleClose}
                  onOpen={handleOpen}
                  filtername={CONDITION}
                >
                  <ConditionFiltersForm
                    inspectionAllowedFilterEnabled={
                      inspectionAllowedFilterEnabled
                    }
                    initialValues={conditionFilterGroup}
                    updateFilters={onFilterChange}
                    submitForms={submitFilterForm}
                    onClose={handleClose}
                    featureFlags={featureFlags}
                  />
                </Popup>
              </div>
              {Boolean(marketingTagsConfig?.length) && (
                <div>
                  <Popup
                    wide
                    {...renderFilterDefaultProps()}
                    trigger={renderTrigger(
                      FEATURED,
                      FILTER_GROUP_KEYS.featuredFilterGroup,
                      FEATURED_ELM_ID
                    )}
                    open={openFilter === FEATURED}
                    onClose={handleClose}
                    onOpen={handleOpen}
                    filtername={FEATURED}
                  >
                    <FeaturedFiltersForm
                      initialValues={featuredFilterGroup}
                      updateFilters={onFilterChange}
                      submitForms={submitFilterForm}
                      onClose={handleClose}
                      marketingTagsConfig={marketingTagsConfig}
                    />
                  </Popup>
                </div>
              )}
              <div>
                <Popup
                  wide
                  {...renderFilterDefaultProps()}
                  trigger={renderTrigger(
                    MORE,
                    FILTER_GROUP_KEYS.moreFilterGroup,
                    MORE_ELM_ID
                  )}
                  open={openFilter === MORE}
                  onClose={handleClose}
                  onOpen={handleOpen}
                  filtername={MORE}
                >
                  <MoreFiltersForm
                    leasebackRequiredFilterEnabled={
                      leasebackRequiredFilterEnabled
                    }
                    initialValues={moreFilterGroup}
                    updateFilters={onFilterChange}
                    submitForms={submitFilterForm}
                    onClose={handleClose}
                  />
                </Popup>
              </div>
              <a
                onClick={clearFiltersAction(showClearFilters)}
                className={cx('clear-filters', !showClearFilters && 'disabled')}
                data-elm-id='clear-filters'
              >
                Clear Filters
              </a>
            </div>

            <div className={cx('mobile-filters-header')}>
              <div className={cx('all-filters')}>
                <Modal
                  mobileTitleSkin='light'
                  trigger={renderTrigger(ALL, null, ALL_ELM_ID)}
                  open={openFilter === ALL}
                  className={cx('filter-modal')}
                  closeOnDimmerClick={false}
                  onClose={onModalClose}
                  onOpen={handleOpen}
                  size='fullscreen'
                  closeIcon={false}
                  filtername={ALL}
                >
                  <Modal.Header>
                    <Grid>
                      <Row>
                        <Column width={8} style={{ marginBottom: 12 }}>
                          <a
                            onClick={clearFiltersAction(showClearFilters)}
                            className={cx(
                              'clear-filters',
                              !showClearFilters && 'disabled'
                            )}
                            data-elm-id='clear-filters'
                          >
                            Clear Filters
                          </a>
                        </Column>
                        <Column width={8} className={cx('filter-close-button')}>
                          <Icon
                            data-elm-id={`${allFiltersBaseElmId}_close_button`}
                            className={cx('u-pointer-cursor')}
                            onClick={onModalClose}
                            iconStyle='light'
                            name='times'
                            fitted
                          />
                        </Column>
                      </Row>
                    </Grid>
                  </Modal.Header>
                  <Modal.Content scrolling>
                    <AllFiltersForm
                      sort={uiFilterGroups.nearbySearchFilterGroup.sort}
                      featureFlags={featureFlags}
                      initialValues={allFiltersInitialValues}
                      updateFilters={onFilterChange}
                      updateSorting={updateSorting}
                      submitForms={submitFilterForm}
                      onClose={handleClose}
                      marketingTagsConfig={marketingTagsConfig}
                    />
                  </Modal.Content>
                </Modal>
              </div>
            </div>
          </>
        </TutorialTooltip>

        <div className={cx('right-side-content')}>
          <TutorialTooltip
            tutorial={5}
            position='bottom right'
            mobilePosition='bottom center'
            nextButtonContent={`Let's Get Started`}
            elmId='notify_me_tutorial'
            header='Never Miss a Listing'
            content='We’ll send you new properties that match your search.'
            handleNext={signup}
          >
            {saveSearchButton}
          </TutorialTooltip>

          <div className={cx('dk-map-view')}>
            <Button
              aria-label='list-map-toggle'
              className={cx('map-toggle-button')}
              elmId={`${BASE_ELM_ID}_toggle_map`}
              onClick={toggleMap}
            >
              <Icon
                name={isMapShown ? 'list' : 'map outline'}
                iconStyle='light'
                size='large'
              />
            </Button>
          </div>
        </div>

        <div className={cx('mb-view-toggle')}>
          {saveSearchButton}
          <Button
            aria-label='list-view-toggle'
            className={cx('popup-button')}
            elmId={`${BASE_ELM_ID}_toggle_view`}
            onClick={toggleView}
          >
            {isMapView && <Icon name='list' iconStyle='light' size='large' />}
            {!isMapView && (
              <Icon name='map outline' iconStyle='light' size='large' />
            )}
          </Button>
        </div>
      </div>
    </div>
  )
}

Filters.propTypes = {
  user: PropTypes.object.isRequired,
  filters: PropTypes.object.isRequired,
  uiFilterGroups: PropTypes.object.isRequired,
  defaultUiFilterGroups: PropTypes.object.isRequired,
  isSearchSaved: PropTypes.bool.isRequired,
  updateSorting: PropTypes.func.isRequired,
  onSaveSearch: PropTypes.func.isRequired,
  featureFlags: PropTypes.object.isRequired,
  onFilterChange: PropTypes.func.isRequired,
  reduxFormSubmit: PropTypes.func.isRequired,
  currentTutorial: PropTypes.number.isRequired,
  marketingTagsConfig: PropTypes.array.isRequired,
}

const mapActions = {
  reduxFormSubmit: submit,
}
const mapProps = store => {
  return {
    user: store.user,
    isSearchSaved: store.savedSearch.isSearchSaved,
    featureFlags: store.featureFlags,
    currentTutorial: store.tutorial.tutorial,
    marketingTagsConfig: store.marketingTagsConfig || [],
  }
}

export default connect(mapProps, mapActions)(Filters)
